import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { IUserLogado } from "../../Services/Interfaces";
import { userServiceRescue } from "../../Services/User";
import {
  ICustomerService,
  IUserServiceRescueIOkRest,
  VitalSignsMeasurementsArray,
} from "../../Services/Interfaces";
import {
  RoundUp,
  intervalsRange,
  verificaTipoExternalId,
} from "../../utils/conversions";

function PageMeasurementThermalText() {
  const { criteriaSrId } = useParams();
  const [userLogado, setUserLogado] = useState<IUserLogado>();
  const [vitalSignsMeasurements, setVitalSignsMeasurements] =
    useState<VitalSignsMeasurementsArray>();
  const [createdAt, setCreatedAt] = useState("");
  const [customerService, setCustomerService] = useState<ICustomerService>();

  useEffect(() => {
    const userJSON = localStorage.getItem("user");
    if (userJSON) {
      const userObjeto = JSON.parse(userJSON) as IUserLogado;
      setUserLogado(userObjeto);
    }
  }, []);

  useEffect(() => {
    if (criteriaSrId) {
      userServiceRescue(undefined, undefined, criteriaSrId).then((response) => {
        const { data } = response as IUserServiceRescueIOkRest;
        if (data.data.length > 0) {
          if (data.data[0].vitalSignsMeasurementsArray) {
            setVitalSignsMeasurements(data.data[0].vitalSignsMeasurementsArray);
            setCreatedAt(data.data[0].createdAt);
            setCustomerService(data.data[0].customerService);
          }
        }
      });
    }
  }, [criteriaSrId]);

  const getStatusFromValue = (
    value: number,
    ranges: number[],
    colors: string[]
  ): string => {
    // Determinar em qual faixa o valor se encontra
    let valueZone = 0;
    
    if (colors.length === 3) {
      // Gráfico de 3 cores
      if (value < ranges[0]) {
        valueZone = 0; // Primeira faixa
      } else if (value >= ranges[1] && value < ranges[2]) {
        valueZone = 1; // Faixa do meio
      } else if (value > ranges[3]) {
        valueZone = 2; // Última faixa
      } else {
        // Valor entre ranges[0] e ranges[1] ou ranges[2] e ranges[3]
        if (value >= ranges[0] && value < ranges[1]) {
          valueZone = 1; // Assume faixa do meio
        } else {
          valueZone = 1; // Assume faixa do meio
        }
      }
    } else if (colors.length === 5) {
      // Gráfico de 5 cores
      if (value < ranges[0]) {
        valueZone = 0;
      } else if (value >= ranges[1] && value < ranges[2]) {
        valueZone = 1;
      } else if (value >= ranges[3] && value < ranges[4]) {
        valueZone = 2;
      } else if (value >= ranges[5] && value < ranges[6]) {
        valueZone = 3;
      } else if (value >= ranges[7]) {
        valueZone = 4;
      } else {
        // Determinar em qual intervalo o valor realmente se encontra
        if (value >= ranges[0] && value < ranges[1]) {
          valueZone = 0;
        } else if (value >= ranges[2] && value < ranges[3]) {
          valueZone = 1;
        } else if (value >= ranges[4] && value < ranges[5]) {
          valueZone = 2;
        } else if (value >= ranges[6] && value < ranges[7]) {
          valueZone = 3;
        } else {
          valueZone = 4;
        }
      }
    }

    // Mapear cor para status usando a mesma lógica do ThermalGraph
    if (colors.length === 5) {
      const color = colors[valueZone];
      
      // Mapear cores para status baseado nas cores reais do HTML
      if (color === "#FF5A56" || color === "#CC444B") {
        return "Alterado"; // Vermelho
      } else if (color === "#ff8280") {
        return "Atenção"; // Vermelho claro
      } else if (color === "#FFBB00") {
        return "Alerta"; // Amarelo
      } else if (color === "#52B788" || color === "#74C69D") {
        return "Bom"; // Verde/Verde claro
      } else if (color === "#40916C") {
        return "Muito Bom"; // Verde escuro
      } else {
        // Fallback baseado na posição
        if (valueZone === 0) return "Alterado";
        else if (valueZone === 1) return "Atenção";
        else if (valueZone === 2) return "Alerta";
        else if (valueZone === 3) return "Bom";
        else return "Muito Bom";
      }
    } else if (colors.length === 3) {
      // Para 3 cores: Alerta, Bom, Alerta
      if (valueZone === 0) return "Alerta";
      else if (valueZone === 1) return "Bom";
      else return "Alerta";
    }
    
    return "";
  };

  const formatThermalTextPrint = () => {
    if (!vitalSignsMeasurements || !userLogado || !customerService) return "";

    let output = "";
    const lineWidth = 65;
    const centerText = (text: string) => {
      const padding = Math.max(0, Math.floor((lineWidth - text.length) / 2));
      return " ".repeat(padding) + text;
    };

    const formatMetricLine = (name: string, unit: string, value: number, status: string) => {
      const metricText = `${name} ${unit} ${value}`;
      const statusText = status ? `=> ${status}` : "";
      const totalContentLength = metricText.length + statusText.length;
      
      if (totalContentLength < lineWidth) {
        const spacesNeeded = lineWidth - totalContentLength;
        return metricText + " ".repeat(spacesNeeded) + statusText;
      } else {
        // Se for muito longo, apenas concatenar
        return metricText + (statusText ? " " + statusText : "");
      }
    };

    // Cabeçalho
    output += "=".repeat(lineWidth) + "\n";
    output += centerText("PROGRAMA " + customerService.program.name.toUpperCase()) + "\n";
    output += "=".repeat(lineWidth) + "\n";
    output += centerText("RESULTADO DA MEDIÇÃO") + "\n";
    output += "-".repeat(lineWidth) + "\n";
    
    // Informações do usuário
    output += `Paciente: ${userLogado.profile.fullName}\n`;
    output += `Data: ${new Date(createdAt).toLocaleDateString()}\n`;
    output += `Hora: ${new Date(createdAt).toLocaleTimeString()}\n`;
    output += "-".repeat(lineWidth) + "\n";

    // SNR se disponível
    if (vitalSignsMeasurements["vazio"]) {
      const snrValue = verificaTipoExternalId("SNR", vitalSignsMeasurements["vazio"], 2);
      output += `SNR: ${snrValue}\n`;
      output += "-".repeat(lineWidth) + "\n";
    }

    // Medições por grupo sem gráficos
    Object.keys(vitalSignsMeasurements).forEach((key) => {
      if (key === "vazio") return;
      const measurements = vitalSignsMeasurements[key];
      if (measurements.length > 0) {
        output += centerText(measurements[0].displayGroup) + "\n";
        output += "-".repeat(lineWidth) + "\n";
        measurements.forEach((vitalSign) => {
          const value = RoundUp(vitalSign.value, parseInt(vitalSign.presentation || "0"));
          if (value === 0) {
            return;
          } else if (intervalsRange(vitalSign.intervalsRange).colors.length > 0) {
            const status = getStatusFromValue(
              value,
              intervalsRange(vitalSign.intervalsRange).ranges,
              intervalsRange(vitalSign.intervalsRange).colors
            );
            
            if (vitalSign.name.toLowerCase().includes("circunferência")) {
              output += centerText(`${vitalSign.name}: ${value} ${vitalSign.unit || ""}`) + "\n";
            } else {
              const formattedLine = formatMetricLine(
                vitalSign.name,
                vitalSign.unit || "",
                value,
                status
              );
              output += formattedLine + "\n";
            }
          } else {
            // Sem intervalos definidos
            if (vitalSign.name.toLowerCase().includes("circunferência")) {
              output += centerText(`${vitalSign.name}: ${value} ${vitalSign.unit || ""}`) + "\n";
            } else {
              output += `${vitalSign.name}: ${value} ${vitalSign.unit || ""}\n`;
            }
          }
        });
        output += "\n"; // Espaço reduzido entre grupos (era \n\n)
      }
    });

    // Rodapé centralizado
    output += centerText("ATENÇÃO!") + "\n";
    output += centerText("Estes índices não deverão substituir a") + "\n";
    output += centerText("avaliação de um profissional de Saúde.") + "\n";
    output += centerText("São indicadores do programa") + "\n";
    output += centerText("de medição de bem-estar.") + "\n";
    output += "=".repeat(lineWidth) + "\n";
    output += "\n\n\n"; // Espaço para cortar papel

    return output;
  };

  const handlePrint = () => {
    if (vitalSignsMeasurements && userLogado && customerService) {
      const thermalContent = formatThermalTextPrint();
      
      // Abrir nova janela com o conteúdo formatado
      const newWindow = window.open("", "_blank");
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head>
              <title>Impressão Térmica Texto</title>
              <style>
                body { 
                  font-family: monospace; 
                  font-size: 14px; 
                  line-height: 1.1;
                  margin: 0;
                  padding: 10px;
                  white-space: pre;
                  font-weight: bold;
                }
              </style>
              <script>
                // Fechar a janela após imprimir ou cancelar
                window.onafterprint = function() {
                  window.close();
                };
                
                // Fechar a janela se o usuário cancelar (detectar mudança de foco)
                window.onbeforeunload = function() {
                  return null;
                };
                
                // Tentar detectar cancelamento de impressão
                let printTimeout;
                window.onfocus = function() {
                  clearTimeout(printTimeout);
                  printTimeout = setTimeout(function() {
                    window.close();
                  }, 1000);
                };
              </script>
            </head>
            <body>${thermalContent}</body>
          </html>
        `);
        newWindow.document.close();
        
        // Aguardar um pouco para o conteúdo carregar antes de imprimir
        setTimeout(() => {
          newWindow.print();
        }, 100);
      }
    }
  };

  return (
    <div style={{ 
      fontFamily: 'monospace', 
      fontSize: '24px', 
      lineHeight: '1.2',
      whiteSpace: 'pre-wrap',
      padding: '20px',
      backgroundColor: '#f5f5f5'
    }}>
      {vitalSignsMeasurements && userLogado && customerService ? (
        <div style={{ textAlign: 'center' }}>
          <h3>Visualização para Impressora Térmica - Texto (80 colunas)</h3>
          <button 
            onClick={handlePrint}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              marginBottom: '20px'
            }}
          >
            Imprimir
          </button>
          <div 
            style={{ 
              backgroundColor: 'white', 
              padding: '20px', 
              border: '1px solid #ccc',
              fontFamily: 'monospace',
              fontSize: '20px',
              whiteSpace: 'pre-wrap',
              lineHeight: '1.2',
              overflow: 'auto',
              textAlign: 'left',
              fontWeight: 'bold'
            }}
            dangerouslySetInnerHTML={{ __html: formatThermalTextPrint().replace(/\n/g, '<br/>') }}
          />
        </div>
      ) : (
        <div>Carregando dados para impressão térmica texto...</div>
      )}
    </div>
  );
}

export default PageMeasurementThermalText;
